// My Approvals — one inbox across workspace requests, eTAMS, letterheads,
// business cards, admin console items and cashbook day close.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Stat, Field, Input, Select, Textarea, Badge, StatusBadge, Modal, Empty, Loading, Spin, Check,
  useAsync, useAction, useDirectory, useSort, Th, usePaged, Pager, money, fmtDate, today, pad2, timeAgo, useQueryParam,
} from '../../components/ui';
import RequestDetail from '../../components/RequestDetail';
import { notify } from '../../utils/notify';

// Workspace request types whose approvers fill stage data (stock check,
// RFQ / PO, desk allocation) — these open on their own module page.
const STAGE_DATA_TYPES = ['it_asset', 'desk', 'material_requisition'];

const MODULE_ICON = { Vendor: 'bi-shop', eTAMS: 'bi-airplane', Requests: 'bi-file-earmark-text', Letterhead: 'bi-file-richtext', 'Admin Console': 'bi-shield-lock', Cashbook: 'bi-journal-text', 'Project Admin': 'bi-kanban', 'IT & Facilities': 'bi-laptop', Procurement: 'bi-cart3', Engineering: 'bi-gear', 'Service Hub': 'bi-grid', 'HR & Admin': 'bi-people', Policies: 'bi-book' };

export default function Approvals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inbox = useAsync(() => wsAPI.inbox(), []);
  const dels = useAsync(() => wsAPI.delegations(), []);
  const directory = useDirectory();
  const [mod, setMod] = useState('');
  const [q, setQ] = useState('');
  const idParam = useQueryParam('id');
  const [detail, setDetail] = useState(idParam);
  // Deep link from an email / bell notification: /approvals?id=<requestId>
  useEffect(() => { if (idParam) setDetail(idParam); }, [idParam]);
  const [delModal, setDelModal] = useState(null);
  const [busy, run] = useAction();
  const items = inbox.data?.items || [];
  const byModule = inbox.data?.byModule || {};
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items.filter((i) => (!mod || i.module === mod) && (!t || [i.refNo, i.title, i.requester, i.kind].some((v) => String(v || '').toLowerCase().includes(t))));
  }, [items, mod, q]);
  const sort = useSort(rows, 'submittedAt', 'desc');
  const paged = usePaged(sort.rows, 15);
  const myDels = (dels.data?.delegations || []).filter((d) => ['Active', 'Pending approval'].includes(d.status));
  const oldest = items.reduce((o, i) => (i.submittedAt && (!o || new Date(i.submittedAt) < new Date(o)) ? i.submittedAt : o), null);
  const overdue = items.filter((i) => i.submittedAt && (Date.now() - new Date(i.submittedAt)) / 86400000 > 2).length;

  const open = (i) => {
    if (i.source === 'workspace' && !STAGE_DATA_TYPES.includes(i.type)) { setDetail(i.id); return; }
    if (i.link) navigate(i.link);
    else if (i.source === 'workspace') setDetail(i.id);
  };
  const saveDel = async () => {
    if (!delModal.toUserId || !delModal.toDate) { notify.error('Choose the delegate and end date'); return; }
    const r = await run('del', () => wsAPI.createDelegation(delModal));
    if (r) { setDelModal(null); dels.reload(); }
  };
  const revoke = async (d) => {
    const r = await run(`rv${d._id}`, () => wsAPI.delegationDecision(d._id, 'revoke'));
    if (r) dels.reload();
  };

  return (
    <PageShell section="TSE Workspace">
      <PageHead crumbs={[{ label: 'Home', to: '/dashboard' }, { label: 'My Approvals' }]} title="My approvals" subtitle="Everything waiting for your decision — across travel, expenses, requests, letters and finance."
        actions={<button type="button" className="tse-btn outline lg" onClick={() => setDelModal({ toUserId: '', fromDate: today(), toDate: '', approvalRequired: true, reason: '' })}><i className="bi bi-person-fill-gear" />Delegate my approvals</button>} />
      <div className="tse-grid g-4 mb-18">
        <Stat icon="bi-inbox-fill" tone="blue" label="Awaiting my action" value={pad2(items.length)} loading={inbox.loading} onClick={() => setMod('')} />
        <Stat icon="bi-exclamation-circle" tone="red" label="Older than 2 days" value={pad2(overdue)} valueTone={overdue ? 'danger' : undefined} loading={inbox.loading} />
        <Stat icon="bi-hourglass-split" tone="amber" label="Oldest pending" value={oldest ? timeAgo(oldest) : '—'} loading={inbox.loading} />
        <Stat icon="bi-person-check" tone="purple" label="Active delegations" value={pad2(myDels.length)} loading={dels.loading} />
      </div>
      {myDels.length > 0 && (
        <Card title="Delegations" icon="bi-person-fill-gear" className="mb-18" bodyClass="tight">
          {myDels.map((d) => (
            <div key={d._id} className="tse-row" style={{ justifyContent: 'space-between', padding: '4px 0' }}>
              <span><b>{d.fromUserName}</b> → <b>{d.toUserName}</b> · {fmtDate(d.fromDate)} – {fmtDate(d.toDate)} <StatusBadge status={d.status === 'Active' ? 'active' : 'pending'} label={d.status} /></span>
              {(String(d.fromUserId) === String(user?.id) || user?.role === 'admin') && <button type="button" className="tse-btn gray sm" disabled={busy === `rv${d._id}`} onClick={() => revoke(d)}>Revoke</button>}
            </div>
          ))}
        </Card>
      )}
      <Card bodyClass="flush">
        <div className="tse-toolbar">
          <div className="tse-filter-pills">
            <button type="button" className={`tse-filter-pill${!mod ? ' active' : ''}`} onClick={() => setMod('')}>All ({items.length})</button>
            {Object.entries(byModule).map(([m, n]) => <button key={m} type="button" className={`tse-filter-pill${mod === m ? ' active' : ''}`} onClick={() => setMod(m)}><i className={`bi ${MODULE_ICON[m] || 'bi-dot'}`} /> {m} ({n})</button>)}
          </div>
          <div className="tse-spacer" />
          <div className="tse-search" style={{ maxWidth: 260 }}><i className="bi bi-search" /><input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} /></div>
          <button type="button" className="tse-icon-btn" onClick={inbox.reload} aria-label="Refresh"><i className="bi bi-arrow-clockwise" /></button>
        </div>
        {inbox.loading ? <Loading /> : !rows.length ? <Empty icon="bi-check2-circle" title="You're all caught up" sub="Nothing is waiting for your approval." /> : (
          <div className="tse-table-wrap"><table className="tse-table">
            <thead><tr><Th sort={sort} k="refNo">Reference</Th><Th sort={sort} k="kind">Type</Th><th>Details</th><Th sort={sort} k="requester">Requester</Th><Th sort={sort} k="amount">Amount</Th><th>Stage</th><Th sort={sort} k="submittedAt">Submitted</Th><th /></tr></thead>
            <tbody>{paged.rows.map((i) => (
              <tr key={`${i.source}-${i.id}`} className="clickable" onClick={() => open(i)}>
                <td className="strong">{i.refNo || '—'}</td>
                <td><span className="tse-row" style={{ gap: 8, flexWrap: 'nowrap' }}><i className={`bi ${MODULE_ICON[i.module] || 'bi-dot'}`} style={{ color: '#1479bd' }} /><span>{i.kind}<div className="tse-small tse-muted">{i.module}</div></span></span></td>
                <td>{i.title}</td><td>{i.requester}</td><td>{i.amount ? money(i.amount) : '—'}</td>
                <td><Badge tone="pending">{String(i.stage || '').replace(/^\w/, (c) => c.toUpperCase())}</Badge></td>
                <td className="nowrap">{fmtDate(i.submittedAt)}<div className="tse-small tse-muted">{i.submittedAt ? timeAgo(i.submittedAt) : ''}</div></td>
                <td><button type="button" className="tse-btn sm" onClick={(e) => { e.stopPropagation(); open(i); }}>Review</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        <Pager {...paged} label="items" />
      </Card>

      <RequestDetail id={detail} onClose={() => { setDetail(null); if (idParam) navigate('/approvals', { replace: true }); }} onChanged={inbox.reload} />
      <Modal open={!!delModal} onClose={() => setDelModal(null)} title="Delegate my approvals" sub="Your delegate can act on your approvals during this period"
        footer={<><button type="button" className="tse-btn gray" onClick={() => setDelModal(null)}>Cancel</button><button type="button" className="tse-btn" disabled={busy === 'del'} onClick={saveDel}>{busy === 'del' ? <Spin /> : null}Set delegation</button></>}>
        {delModal && (
          <div className="tse-form-grid c2">
            <Field label="Delegate to" required className="span-all"><Select value={delModal.toUserId} placeholder="Select colleague" onChange={(e) => setDelModal((m) => ({ ...m, toUserId: e.target.value }))} options={directory.filter((u) => String(u._id) !== String(user?.id)).map((u) => ({ value: u._id, label: `${u.userName} · ${u.designation || u.department || ''}` }))} /></Field>
            <Field label="From" required><Input type="date" min={today()} value={delModal.fromDate} onChange={(e) => setDelModal((m) => ({ ...m, fromDate: e.target.value }))} /></Field>
            <Field label="To" required><Input type="date" min={delModal.fromDate} value={delModal.toDate} onChange={(e) => setDelModal((m) => ({ ...m, toDate: e.target.value }))} /></Field>
            <Field label="Reason" className="span-all"><Textarea rows={2} value={delModal.reason} onChange={(e) => setDelModal((m) => ({ ...m, reason: e.target.value }))} placeholder="e.g. On leave / site visit" /></Field>
            <div className="span-all"><Check checked={delModal.approvalRequired} disabled={user?.role === 'admin'} onChange={(v) => setDelModal((m) => ({ ...m, approvalRequired: v }))} label="Require administrator approval" /></div>
          </div>
        )}
      </Modal>
    </PageShell>
  );
}
